/**
 * Deepgram STT Provider
 *
 * Implements the STTProvider interface for Deepgram's Nova-3 (and other)
 * streaming speech-to-text models via WebSocket.
 *
 * Authentication uses the WebSocket sub-protocol approach, passing the
 * ephemeral JWT token as ['Bearer', jwt]. The 'Bearer' scheme is required
 * for JWT tokens from /v1/auth/grant (vs 'token' for raw API keys).
 * This avoids CORS issues since browsers cannot set custom headers on
 * WebSocket connections.
 *
 * Connection lifecycle:
 *   - Open per-turn (when user starts speaking)
 *   - KeepAlive every 5s to prevent 10-second timeout
 *   - Finalize to flush buffered results
 *   - Close after receiving final transcript (cost management)
 */

import type { STTConfig, STTEvents, STTProvider } from '@/types/voice'

export class DeepgramSTTProvider implements STTProvider {
  readonly name = 'deepgram'
  /** Deepgram documents that the Finalize answer "is not guaranteed if there is no significant amount of audio data to process" */
  readonly alwaysAnswersFinalize = false
  private ws: WebSocket | null = null
  private events: STTEvents
  private keepAliveIntervalId: ReturnType<typeof setInterval> | null = null
  /** Accumulated audio duration from is_final Results (seconds) */
  private _totalDurationSec = 0

  constructor(events: STTEvents) {
    this.events = events
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  /** Total audio duration processed in this session (ms) */
  get totalDurationMs(): number {
    return this._totalDurationSec * 1000
  }

  /** Model name actually used (from last Results metadata) */
  private _modelUsed = ''
  get modelUsed(): string {
    return this._modelUsed
  }

  async connect(config: STTConfig): Promise<void> {
    this._totalDurationSec = 0
    // Cost rate key: nova-3 mono vs nova-3 multi based on language code.
    // Deepgram bills mono and multi at different rates; our pricingRates.ts
    // has 'nova-3' and 'nova-3-multi' keys to match.
    this._modelUsed = config.language === 'multi' ? 'nova-3-multi' : 'nova-3'

    const params = new URLSearchParams({
      model: config.model || 'nova-3',
      language: config.language,
      smart_format: 'true',
      punctuate: 'true',
      interim_results: String(config.interimResults),
      endpointing: String(config.endpointingMs || 300),
      utterance_end_ms: String(config.utteranceEndMs || 1000),
      encoding: config.encoding,
      sample_rate: String(config.sampleRate),
      channels: '1',
      vad_events: 'true',
    })

    return new Promise<void>((resolve, reject) => {
      // Authenticate via WebSocket sub-protocol (browser-friendly)
      // Use 'Bearer' scheme for JWT tokens from /v1/auth/grant
      this.ws = new WebSocket(
        `wss://api.deepgram.com/v1/listen?${params}`,
        ['Bearer', config.token]
      )

      this.ws.onopen = () => {
        this.events.onOpen()
        // Start KeepAlive to prevent 10-second timeout
        this.startKeepAlive()
        resolve()
      }

      this.ws.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data as string)

          if (data.type === 'Results') {
            const transcript =
              data.channel?.alternatives?.[0]?.transcript || ''

            // Track audio duration from final results for cost tracking
            if (data.is_final && data.duration) {
              this._totalDurationSec += data.duration
            }

            this.events.onTranscript(
              transcript,
              data.is_final ?? false,
              data.speech_final ?? false,
              data.from_finalize === true
            )
          }

          if (data.type === 'UtteranceEnd') {
            this.events.onUtteranceEnd()
          }

          // Metadata summary (sent on close) — contains total session duration
          if (data.type === 'Metadata' && data.duration) {
            this._totalDurationSec = data.duration
          }
        } catch {
          // Ignore unparseable messages
        }
      }

      this.ws.onerror = () => {
        this.events.onError(
          new Error('Deepgram WebSocket connection error')
        )
        reject(new Error('Deepgram connection failed'))
      }

      this.ws.onclose = () => {
        this.stopKeepAlive()
        this.events.onClose()
      }
    })
  }

  sendAudio(pcmData: ArrayBuffer): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(pcmData)
    }
  }

  keepAlive(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'KeepAlive' }))
    }
  }

  finalize(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'Finalize' }))
    }
  }

  close(): void {
    this.stopKeepAlive()
    if (this.ws?.readyState === WebSocket.OPEN) {
      // Send CloseStream to flush buffered results before closing
      this.ws.send(JSON.stringify({ type: 'CloseStream' }))
    }
    this.ws?.close()
    this.ws = null
  }

  private startKeepAlive(): void {
    this.stopKeepAlive()
    // Send KeepAlive every 5 seconds to prevent 10-second timeout
    this.keepAliveIntervalId = setInterval(() => this.keepAlive(), 5000)
  }

  private stopKeepAlive(): void {
    if (this.keepAliveIntervalId) {
      clearInterval(this.keepAliveIntervalId)
      this.keepAliveIntervalId = null
    }
  }
}
